import React, { useState, useEffect, useCallback } from "react";
import Table from "../../components/Table";
import CommonHeader from "../../components/CommonHeader";
import Modal from "../../components/Modal";
import { FaFileInvoice, FaEye, FaSearch, FaTimes, FaDownload } from "react-icons/fa";
import { getEnrollmentsList, getInvoiceDownloadUrl } from "../../services/authService";
import Swal from "sweetalert2";

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    enroll_type: "",
    status: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    enroll_type: "",
    status: "",
  });

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // Fetch enrollments list from API
  const fetchOrders = useCallback(async (page = 1, limit = 10, filterParams = appliedFilters) => {
    setLoading(true);
    try {
      const payload = {
        page,
        limit,
        search: filterParams.search || "",
        startDate: filterParams.startDate || "",
        endDate: filterParams.endDate || "",
        enroll_type: filterParams.enroll_type || "",
        status: filterParams.status || "",
      };

      const res = await getEnrollmentsList(payload);
      if (res && res.data) {
        setOrders(res.data || []);
        setTotalCount(res.totalCount || res.data.length || 0);
        setTotalPages(res.totalPages || Math.ceil((res.totalCount || res.data.length) / limit) || 1);
        setCurrentPage(page);
      } else {
        setOrders([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching enrollments/orders list:", err);
      setOrders([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchOrders(currentPage, pageLimit, appliedFilters);
  }, [fetchOrders, currentPage, pageLimit, appliedFilters]);

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchOrders(1, pageLimit, filters);
  };

  const handleClear = () => {
    const emptyFilters = {
      search: "",
      startDate: "",
      endDate: "",
      enroll_type: "",
      status: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
    fetchOrders(1, pageLimit, emptyFilters);
  };

  const handleDownloadInvoice = async (order) => {
    const billingId = order.billing_id || order.billing?.billing_id;
    if (!billingId) {
      Swal.fire({
        icon: "warning",
        title: "Invoice Not Available",
        text: "Billing record is not linked to this order yet.",
        confirmButtonColor: "#1A3C8B",
      });
      return;
    }

    try {
      setDownloadingId(order._id || order.enroll_id);
      const invoiceUrl = getInvoiceDownloadUrl(billingId);

      // Trigger download in new tab
      window.open(invoiceUrl, "_blank");
    } catch (err) {
      console.error("Invoice download failed:", err);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download invoice. Please try again.",
        confirmButtonColor: "#1A3C8B",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewModalOpen(true);
  };

  const columns = [
    { header: "S.No", accessor: "s_no" },
    { header: "Date & Time", accessor: "date" },
    { header: "Student Name", accessor: "name" },
    { header: "Contact", accessor: "contact" },
    { header: "Course Title", accessor: "course_title" },
    { header: "Type", accessor: "enroll_type_badge" },
    { header: "Duration", accessor: "duration" },
    { header: "Paid Amount", accessor: "paid_amount" },
    { header: "Status", accessor: "status_badge" },
    { header: "Actions", accessor: "actions" },
  ];

  const getEnrollTypeBadgeColor = (type) => {
    switch (type) {
      case "full-course":
        return { bg: "#e8efff", text: "#1A3C8B", border: "#c3d7ff" };
      case "combination":
        return { bg: "#fef9e7", text: "#946c00", border: "#fde8a1" };
      case "mains":
        return { bg: "#fcedec", text: "#8b1a1a", border: "#f7c5c2" };
      case "prelimes":
        return { bg: "#e8f8f5", text: "#0e6251", border: "#a3e4d7" };
      case "notes":
        return { bg: "#f5eef8", text: "#5b2c6f", border: "#d7bde2" };
      default:
        return { bg: "#f2f3f4", text: "#424949", border: "#d5d8dc" };
    }
  };

  const tableData = orders.map((order, index) => {
    const typeStyle = getEnrollTypeBadgeColor(order.enroll_type);
    const dateFormatted = order.formatted_enrollment_date || order.enrollment_date || "N/A";
    const timeFormatted = order.formatted_enrollment_time || order.enrollment_time || "";

    return {
      id: order._id || order.enroll_id || index,
      s_no: (currentPage - 1) * pageLimit + index + 1,

      date: (
        <div>
          <div style={{ fontWeight: "600" }}>{dateFormatted}</div>
          {timeFormatted && <small className="text-muted" style={{ fontSize: "11px" }}>{timeFormatted}</small>}
        </div>
      ),
      name: <span style={{ fontWeight: "600", color: "#1A3C8B" }}>{order.user?.name || "N/A"}</span>,
      contact: (
        <div>
          <div>{order.user?.mobile_number || "N/A"}</div>
          {order.user?.email && <small className="text-muted" style={{ fontSize: "11px" }}>{order.user?.email}</small>}
        </div>
      ),
      course_title: (
        <span
          title={order.courseDetails?.title || "N/A"}
          style={{
            maxWidth: "180px",
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: "500",
          }}
        >
          {order.courseDetails?.title || "Course Enrollment"}
        </span>
      ),
      enroll_type_badge: (
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "bold",
            textTransform: "uppercase",
            background: typeStyle.bg,
            color: typeStyle.text,
            border: `1px solid ${typeStyle.border}`,
          }}
        >
          {order.enroll_type || "N/A"}
        </span>
      ),
      duration: order.plan?.duration || order.billing?.billing_cycle || "1 Year",
      paid_amount: (
        <span style={{ fontWeight: "bold", color: "#2b8a3e" }}>
          ₹ {Number(order.final_price !== undefined ? order.final_price : (order.billing?.amount || 0)).toLocaleString("en-IN")}
        </span>
      ),
      status_badge: (
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "bold",
            background: order.status === "active" ? "#e6f4ea" : "#fce8e6",
            color: order.status === "active" ? "#137333" : "#c5221f",
          }}
        >
          {order.status || "active"}
        </span>
      ),
      actions: (
        <div className="actions d-flex gap-2">
          <button
            className="view"
            onClick={() => handleViewOrder(order)}
            title="View Order Details"
          >
            <FaEye />
          </button>
          <button
            className="edit"
            onClick={() => handleDownloadInvoice(order)}
            title="Download Invoice"
            disabled={downloadingId === (order._id || order.enroll_id)}
          >
            <FaFileInvoice />
          </button>
        </div>
      ),
    };
  });

  return (
    <div>
      <CommonHeader
        title="ORDERS & ENROLLMENTS LIST"
        count={totalCount}
        totalPages={totalPages}
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        setCurrentPage={setCurrentPage}
        onChange={(page, limit) => {
          setCurrentPage(page);
          setPageLimit(limit);
          fetchOrders(page, limit, appliedFilters);
        }}
        infoText="💡 View student orders, payment status, enrolled courses and download tax invoices."
      />

      {/* 🔍 Search & Filter Box */}
      <div
        style={{
          padding: "16px",
          background: "#fdf8ee",
          margin: "0 16px 16px",
          borderRadius: "8px",
          border: "1px solid #f2e3c6",
        }}
      >
        <div className="row g-3 align-items-end">
          {/* Row 1: Search & Type */}
          <div className="col-md-4">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Search (Name / Mobile / Email / Order ID)
            </label>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <FaSearch className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Enrollment Type
            </label>
            <select
              className="form-select"
              value={filters.enroll_type}
              onChange={(e) => setFilters({ ...filters, enroll_type: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="full-course">Full Course</option>
              <option value="combination">Combination</option>
              <option value="prelimes">Prelims</option>
              <option value="mains">Mains</option>
              <option value="notes">Notes</option>
              <option value="subject-wise">Subject Wise</option>
              <option value="subcategory-wise">Subcategory Wise</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Status
            </label>
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Row 2: Date Range */}
          <div className="col-md-4">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Start Date
            </label>
            <input
              type="date"
              className="form-control"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              End Date
            </label>
            <input
              type="date"
              className="form-control"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>

          <div className="col-md-4 d-flex justify-content-end gap-2">
            <button className="btn btn-secondary px-4" onClick={handleClear} disabled={loading}>
              <FaTimes className="me-1" /> Clear
            </button>
            <button
              className="btn text-white px-4"
              onClick={handleSearch}
              disabled={loading}
              style={{ background: "#1a2744", borderColor: "#1a2744" }}
            >
              <FaSearch className="me-1" /> {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => {
          setCurrentPage(page);
          fetchOrders(page, pageLimit, appliedFilters);
        }}
        isLoading={loading}
      />

      {/* 📄 Order & Enrollment Details Modal */}
      {selectedOrder && (
        <Modal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          title="Order & Enrollment Details"
          size="lg"
        >
          <div className="p-3">
            {/* Top Status & Date Banner */}
            <div
              className="d-flex justify-content-between align-items-center p-3 mb-4 rounded"
              style={{ background: "#f0f4ff", borderLeft: "4px solid #1A3C8B" }}
            >
              <div>
                <span className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>
                  Enrolled Course
                </span>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#1A3C8B" }}>
                  {selectedOrder.courseDetails?.title || "Course Enrollment"}
                </div>
              </div>
              <div className="text-end">
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    background: selectedOrder.status === "active" ? "#e6f4ea" : "#fce8e6",
                    color: selectedOrder.status === "active" ? "#137333" : "#c5221f",
                  }}
                >
                  {selectedOrder.status || "active"}
                </span>
                <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                  Date: {selectedOrder.formatted_enrollment_date || selectedOrder.enrollment_date || "N/A"}
                </div>
              </div>
            </div>

            <div className="row g-4">
              {/* Student Information */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100 bg-white">
                  <h6 className="fw-bold mb-3" style={{ color: "#1A3C8B" }}>
                    Student Information
                  </h6>
                  <p className="mb-2">
                    <strong className="text-muted">Name:</strong> {selectedOrder.user?.name || "N/A"}
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Mobile Number:</strong> {selectedOrder.user?.mobile_number || "N/A"}
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Email ID:</strong> {selectedOrder.user?.email || "N/A"}
                  </p>
                  <p className="mb-0">
                    <strong className="text-muted">Role:</strong>{" "}
                    <span className="badge bg-secondary text-capitalize">{selectedOrder.user?.role || "Student"}</span>
                  </p>
                </div>
              </div>

              {/* Course Details */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100 bg-white">
                  <h6 className="fw-bold mb-3" style={{ color: "#1A3C8B" }}>
                    Course Details
                  </h6>
                  <p className="mb-2">
                    <strong className="text-muted">Course Name:</strong>{" "}
                    <span className="fw-bold">{selectedOrder.courseDetails?.title || "N/A"}</span>
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Category:</strong>{" "}
                    {selectedOrder.courseDetails?.category?.category_name || "N/A"}
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Type:</strong>{" "}
                    <span className="badge bg-primary text-uppercase">{selectedOrder.enroll_type || "N/A"}</span>
                  </p>
                  <p className="mb-0">
                    <strong className="text-muted">Expiry Date:</strong>{" "}
                    {selectedOrder.expiry_date || selectedOrder.billing?.valid_till || "N/A"}
                  </p>
                </div>
              </div>

              {/* Plan & Pricing */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100 bg-white">
                  <h6 className="fw-bold mb-3" style={{ color: "#1A3C8B" }}>
                    Plan & Subscription
                  </h6>
                  <p className="mb-2">
                    <strong className="text-muted">Duration:</strong>{" "}
                    {selectedOrder.plan?.duration || selectedOrder.billing?.billing_cycle || "1 Year"}
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Original Price:</strong>{" "}
                    <span className="text-decoration-line-through">
                      ₹ {selectedOrder.plan?.strike_price || selectedOrder.plan?.original_price || "0"}
                    </span>
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Final Paid Amount:</strong>{" "}
                    <span className="fw-bold text-success fs-5">
                      ₹ {Number(selectedOrder.final_price !== undefined ? selectedOrder.final_price : (selectedOrder.billing?.amount || 0)).toLocaleString("en-IN")}
                    </span>
                  </p>
                  {selectedOrder.coupon_code && (
                    <p className="mb-0">
                      <strong className="text-muted">Applied Coupon:</strong>{" "}
                      <span className="badge bg-warning text-dark">{selectedOrder.coupon_code}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Payment & Billing Information */}
              <div className="col-md-6">
                <div className="border rounded p-3 h-100 bg-white">
                  <h6 className="fw-bold mb-3" style={{ color: "#1A3C8B" }}>
                    Payment & Billing
                  </h6>
                  <p className="mb-2">
                    <strong className="text-muted">Payment ID:</strong>{" "}
                    <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                      {selectedOrder.payment_id || "N/A"}
                    </span>
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Payment Status:</strong>{" "}
                    <span className="badge bg-success">
                      {selectedOrder.billing?.billing_status || "paid"}
                    </span>
                  </p>
                  <p className="mb-2">
                    <strong className="text-muted">Currency:</strong>{" "}
                    {selectedOrder.billing?.currency || "INR"}
                  </p>
                  <p className="mb-0">
                    <strong className="text-muted">Transaction Date:</strong>{" "}
                    {selectedOrder.formatted_enrollment_date || selectedOrder.enrollment_date || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <button className="btn btn-secondary" onClick={() => setViewModalOpen(false)}>
                Close
              </button>
              {(selectedOrder.billing_id || selectedOrder.billing?.billing_id) && (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={() => handleDownloadInvoice(selectedOrder)}
                  style={{ background: "#1A3C8B", borderColor: "#1A3C8B" }}
                >
                  <FaDownload /> Download Invoice PDF
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Orders;
